// Google Analytics 4 Service
// Replaces the complex custom analytics system

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

export class GoogleAnalyticsService {
  private static initialized = false;
  private static isProduction = process.env.NODE_ENV === 'production';
  // Production fallback: Use the actual GA4 measurement ID if env variable fails
  private static measurementId =
    process.env.REACT_APP_GA_MEASUREMENT_ID ||
    (process.env.NODE_ENV === 'production' ? 'G-SPZ4F13J47' : null);

  /**
   * Safe wrapper for gtag calls
   */
  private static safeGtag(command: string, ...args: any[]): void {
    if (!this.isProduction || !this.measurementId) return;

    if (typeof window.gtag === 'function') {
      window.gtag(command, ...args);
    } else {
      // Queue the command for when GA loads
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(arguments);
    }
  }

  /**
   * Initialize Google Analytics (called once on app start)
   * Uses standard async/defer loading for reliability and performance
   */
  static init(): void {
    // BOT DETECTION - Skip GA initialization for bots to improve SEO4Ajax capture time
    const botPattern =
      /googlebot|bingbot|slurp|duckduckbot|baiduspider|yandexbot|facebookexternalhit|twitterbot|rogerbot|linkedinbot|embedly|quora link preview|showyoubot|outbrain|pinterest\/0\.|pinterestbot|developers\.google\.com\/\+\/web\/snippet|whatsapp|flipboard|tumblr|bitlybot|skypeuripreview|nuzzel|discordbot|google page speed|qwantify|bitrix link preview|xing-contenttabreceiver|chrome-lighthouse|telegrambot|seo4ajax|api\.seo4ajax/i;

    if (botPattern.test(navigator.userAgent.toLowerCase())) {
      console.log('[GA] Skipping initialization for bot user agent');
      return;
    }
    // END BOT DETECTION

    // Debug logging for GA initialization
    if (this.isProduction) {
      console.log('[GA] Initializing Google Analytics', {
        measurementId: this.measurementId,
        partytownRemoved: true,
        loadingMethod: 'async/defer',
        isProduction: this.isProduction,
      });
    }

    if (this.initialized || !this.isProduction || !this.measurementId) {
      if (this.isProduction && !this.measurementId) {
        console.error('[GA] No measurement ID found - analytics will not track');
      }
      return;
    }

    this.initialized = true;

    // Initialize dataLayer and gtag function
    // These are forwarded to the Web Worker by Partytown
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () {
      window.dataLayer.push(arguments);
    };
    window.gtag('js', new Date());

    // Load Google Analytics script with async/defer for optimal performance
    const script = document.createElement('script');
    script.async = true;
    script.defer = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this.measurementId}`;
    document.head.appendChild(script);

    // Configure GA4 - now runs in main thread for reliability
    window.gtag('config', this.measurementId, {
      send_page_view: false, // We'll track page views manually for SPA
      custom_map: {
        custom_parameter_1: 'user_type',
        custom_parameter_2: 'deal_category',
      },
    });

    console.log(`[GA] Google Analytics initialized successfully with ID: ${this.measurementId}`);
    console.log('[GA] Script loaded with async/defer for optimal performance');
    console.log('[GA] Check Network tab for /g/collect requests to verify tracking');
  }

  /**
   * Track page views (replaces trackPageView)
   */
  static trackPageView(pagePath: string, pageTitle?: string): void {
    this.safeGtag('config', this.measurementId, {
      page_path: pagePath,
      page_title: pageTitle || document.title,
    });
  }

  /**
   * Track deal views (replaces trackDealView)
   */
  static trackDealView(dealId: string, dealTitle: string, category: string): void {
    this.safeGtag('event', 'view_item', {
      item_id: dealId,
      item_name: dealTitle,
      item_category: category,
      content_type: 'deal',
    });
  }

  /**
   * Track affiliate clicks (replaces trackDealClick)
   */
  static trackAffiliateClick(dealId: string, dealTitle: string, affiliateUrl: string): void {
    this.safeGtag('event', 'click', {
      event_category: 'affiliate',
      event_label: dealTitle,
      custom_parameter_1: dealId,
      external_url: affiliateUrl,
    });
  }

  /**
   * Track searches (replaces trackSearch)
   */
  static trackSearch(searchQuery: string, resultsCount: number): void {
    this.safeGtag('event', 'search', {
      search_term: searchQuery,
      custom_parameter_1: resultsCount.toString(),
    });
  }

  /**
   * Track user signups (replaces user signup tracking)
   */
  static trackSignup(method: string = 'email'): void {
    this.safeGtag('event', 'sign_up', {
      method: method,
    });
  }

  /**
   * Track user login
   */
  static trackLogin(method: string = 'email'): void {
    this.safeGtag('event', 'login', {
      method: method,
    });
  }

  /**
   * Track deal submissions
   */
  static trackDealSubmission(category: string): void {
    this.safeGtag('event', 'submit_deal', {
      event_category: 'engagement',
      event_label: category,
    });
  }

  /**
   * Track voting
   */
  static trackVote(dealId: string, voteType: 'up' | 'down'): void {
    this.safeGtag('event', 'vote', {
      event_category: 'engagement',
      event_label: voteType,
      custom_parameter_1: dealId,
    });
  }

  /**
   * Set user properties (when user logs in)
   */
  static setUserProperties(userId: string, userType: 'free' | 'premium' = 'free'): void {
    this.safeGtag('config', this.measurementId, {
      user_id: userId,
      custom_parameter_1: userType,
    });
  }

  /**
   * Track errors (replaces error tracking)
   */
  static trackError(errorMessage: string, errorLocation: string): void {
    this.safeGtag('event', 'exception', {
      description: errorMessage,
      fatal: false,
      custom_parameter_2: errorLocation,
    });
  }

  /**
   * Track time on page
   */
  static trackTimeOnPage(
    pageType: string,
    timeSeconds: number,
    metadata?: Record<string, any>
  ): void {
    this.safeGtag('event', 'time_on_page', {
      page_type: pageType,
      time_seconds: timeSeconds,
      ...metadata,
    });
  }

  /**
   * Track scroll depth
   */
  static trackScrollDepth(depth: number, pageType: string): void {
    this.safeGtag('event', 'scroll', {
      percent_scrolled: depth,
      page_type: pageType,
    });
  }

  /**
   * Track filter usage
   */
  static trackFilterUsage(filterType: string, filterValue: string, resultsCount: number): void {
    this.safeGtag('event', 'filter_usage', {
      filter_type: filterType,
      filter_value: filterValue,
      results_count: resultsCount,
    });
  }
}

// Simple replacement for complex analytics
export const analytics = {
  init: () => GoogleAnalyticsService.init(),
  trackPageView: (path: string) => GoogleAnalyticsService.trackPageView(path),
  trackDealView: (dealId: string, title: string, category: string) =>
    GoogleAnalyticsService.trackDealView(dealId, title, category),
  trackAffiliateClick: (dealId: string, title: string, url: string) =>
    GoogleAnalyticsService.trackAffiliateClick(dealId, title, url),
  trackSearch: (query: string, count: number) => GoogleAnalyticsService.trackSearch(query, count),
  trackSignup: () => GoogleAnalyticsService.trackSignup(),
  trackLogin: () => GoogleAnalyticsService.trackLogin(),
  trackVote: (dealId: string, type: 'up' | 'down') =>
    GoogleAnalyticsService.trackVote(dealId, type),
  setUser: (userId: string) => GoogleAnalyticsService.setUserProperties(userId),
  trackError: (message: string, location: string) =>
    GoogleAnalyticsService.trackError(message, location),
  trackTimeOnPage: (pageType: string, seconds: number, metadata?: any) =>
    GoogleAnalyticsService.trackTimeOnPage(pageType, seconds, metadata),
  trackScrollDepth: (depth: number, pageType: string) =>
    GoogleAnalyticsService.trackScrollDepth(depth, pageType),
  trackFilterUsage: (type: string, value: string, count: number) =>
    GoogleAnalyticsService.trackFilterUsage(type, value, count),
};
